import { ApparatDeviceBox, AudioFileBox, NeonDeviceBox, MIDIOutputDeviceBox, NanoDeviceBox, PlayfieldDeviceBox, PlayfieldSampleBox, SoundfontDeviceBox, SoundfontFileBox, TapeDeviceBox, VaporisateurDeviceBox } from "@opendaw/studio-boxes";
import { isDefined, UUID } from "@opendaw/lib-std";
import { ClassicWaveform } from "@opendaw/lib-dsp";
import { IconSymbol, VoicingMode } from "@opendaw/studio-enums";
import { DeviceManualUrls } from "../DeviceManualUrls";
import { TrackType } from "../timeline/TrackType";
export var InstrumentFactories;
(function (InstrumentFactories) {
    InstrumentFactories.Tape = {
        defaultName: "Tape",
        defaultIcon: IconSymbol.Tape,
        briefDescription: "Audio Player",
        description: "Plays audio regions & clips",
        manualPage: DeviceManualUrls.Tape,
        trackType: TrackType.Audio,
        create: (boxGraph, host, name, icon, _attachment) => TapeDeviceBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue(name);
            box.icon.setValue(IconSymbol.toName(icon));
            box.flutter.setValue(0.2);
            box.wow.setValue(0.05);
            box.noise.setValue(0.02);
            box.saturation.setValue(0.5);
            box.host.refer(host);
        })
    };
    InstrumentFactories.Nano = {
        defaultName: "Nano",
        defaultIcon: IconSymbol.NanoWave,
        briefDescription: "Simple Sampler",
        description: "Simple sampler",
        manualPage: DeviceManualUrls.Nano,
        trackType: TrackType.Notes,
        create: (boxGraph, host, name, icon, attachment) => NanoDeviceBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue(name);
            box.icon.setValue(IconSymbol.toName(icon));
            if (isDefined(attachment)) {
                box.file.refer(attachment);
            }
            box.host.refer(host);
        })
    };
    InstrumentFactories.Playfield = {
        defaultName: "Playfield",
        defaultIcon: IconSymbol.Playfield,
        briefDescription: "Drum Machine",
        description: "Drum computer",
        manualPage: DeviceManualUrls.Playfield,
        trackType: TrackType.Notes,
        create: (boxGraph, host, name, icon, attachment) => {
            const deviceBox = PlayfieldDeviceBox.create(boxGraph, UUID.generate(), box => {
                box.label.setValue(name);
                box.icon.setValue(IconSymbol.toName(icon));
                box.host.refer(host);
            });
            if (isDefined(attachment)) {
                attachment.filter(({ note, uuid, name, durationInSeconds, exclude }) => {
                    const fileBox = useAudioFile(boxGraph, uuid, name, durationInSeconds);
                    PlayfieldSampleBox.create(boxGraph, UUID.generate(), box => {
                        box.device.refer(deviceBox.samples);
                        box.file.refer(fileBox);
                        box.index.setValue(note);
                        box.exclude.setValue(exclude);
                    });
                });
            }
            return deviceBox;
        }
    };
    InstrumentFactories.Neon = {
        defaultName: "Neon",
        defaultIcon: IconSymbol.Neon,
        briefDescription: "CZ-style Synth",
        description: "CZ-style phase distortion synthesizer",
        manualPage: DeviceManualUrls.Neon,
        trackType: TrackType.Notes,
        create: (boxGraph, host, name, icon, _attachment) => NeonDeviceBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue(name);
            box.icon.setValue(IconSymbol.toName(icon));
            // The init tone: line 1 saw, DCW fully open, organ-style DCA (full until note-off, short
            // release stage). Envelope array order: line1 pitch/DCW/DCA, line2 pitch/DCW/DCA.
            for (const line of [0, 1]) {
                const dcw = box.envelopes.fields()[line * 3 + 1];
                dcw.rate1.setInitValue(99);
                dcw.level1.setInitValue(99);
                dcw.sustain.setInitValue(1);
                dcw.end.setInitValue(2);
                dcw.rate2.setInitValue(99);
                const dca = box.envelopes.fields()[line * 3 + 2];
                dca.rate1.setInitValue(99);
                dca.level1.setInitValue(99);
                dca.sustain.setInitValue(1);
                dca.end.setInitValue(2);
                dca.rate2.setInitValue(70);
            }
            box.host.refer(host);
        })
    };
    InstrumentFactories.Vaporisateur = {
        defaultName: "Vaporisateur",
        defaultIcon: IconSymbol.Piano,
        briefDescription: "Subtractive Synth",
        description: "Classic subtractive synthesizer",
        manualPage: DeviceManualUrls.Vaporisateur,
        trackType: TrackType.Notes,
        create: (boxGraph, host, name, icon, _attachment) => VaporisateurDeviceBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue(name);
            box.icon.setValue(IconSymbol.toName(icon));
            box.cutoff.setInitValue(8000.0);
            box.resonance.setInitValue(0.1);
            box.attack.setInitValue(0.005);
            box.decay.setInitValue(0.100);
            box.sustain.setInitValue(0.5);
            box.release.setInitValue(0.5);
            box.voicingMode.setInitValue(VoicingMode.Polyphonic);
            box.lfo.rate.setInitValue(1.0);
            box.oscillators.fields()[0].waveform.setInitValue(ClassicWaveform.saw);
            box.oscillators.fields()[0].volume.setInitValue(-6.0);
            box.oscillators.fields()[1].volume.setInitValue(Number.NEGATIVE_INFINITY);
            box.oscillators.fields()[1].waveform.setInitValue(ClassicWaveform.square);
            box.host.refer(host);
            box.version.setValue(2); // for removing the -15db in voice and extended osc
        })
    };
    InstrumentFactories.MIDIOutput = {
        defaultName: "MIDIOutput",
        defaultIcon: IconSymbol.Midi,
        briefDescription: "Send MIDI",
        description: "MIDI Output",
        manualPage: DeviceManualUrls.MIDIOutput,
        trackType: TrackType.Notes,
        create: (boxGraph, host, name, icon, _attachment) => MIDIOutputDeviceBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue(name);
            box.icon.setValue(IconSymbol.toName(icon));
            box.host.refer(host);
        })
    };
    InstrumentFactories.Soundfont = {
        defaultName: "Soundfont",
        defaultIcon: IconSymbol.SoundFont,
        briefDescription: "Soundfont Player",
        description: "Soundfont Player",
        manualPage: DeviceManualUrls.Soundfont,
        trackType: TrackType.Notes,
        create: (boxGraph, host, name, icon) => SoundfontDeviceBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue(name);
            box.icon.setValue(IconSymbol.toName(icon));
            box.host.refer(host);
        })
    };
    InstrumentFactories.Apparat = {
        defaultName: "Apparat",
        defaultIcon: IconSymbol.Code,
        briefDescription: "Scriptable Instrument",
        description: "User-scripted instrument",
        manualPage: DeviceManualUrls.Apparat,
        trackType: TrackType.Notes,
        create: (boxGraph, host, name, icon) => ApparatDeviceBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue(name);
            box.icon.setValue(IconSymbol.toName(icon));
            box.host.refer(host);
        })
    };
    InstrumentFactories.Named = { Apparat: InstrumentFactories.Apparat, Neon: InstrumentFactories.Neon, MIDIOutput: InstrumentFactories.MIDIOutput, Nano: InstrumentFactories.Nano, Playfield: InstrumentFactories.Playfield, Soundfont: InstrumentFactories.Soundfont, Tape: InstrumentFactories.Tape, Vaporisateur: InstrumentFactories.Vaporisateur };
    const useAudioFile = (boxGraph, fileUUID, name, duration) => boxGraph.findBox(fileUUID)
        .unwrapOrElse(() => AudioFileBox.create(boxGraph, fileUUID, box => {
        box.fileName.setValue(name);
        box.endInSeconds.setValue(duration);
    }));
    const useSoundfontFile = (boxGraph, fileUUID, name) => boxGraph.findBox(fileUUID)
        .unwrapOrElse(() => SoundfontFileBox.create(boxGraph, fileUUID, box => box.fileName.setValue(name)));
})(InstrumentFactories || (InstrumentFactories = {}));
